import { useEffect, useState } from "react";
import { Lock, LogOut, Sparkles, Trash2, RefreshCw, Mail, Phone, MapPin, Users, Calendar, BarChart3, Eye, MessageCircle, Smartphone, Monitor } from "lucide-react";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { adminLogin, fetchBookings, updateBookingStatus, deleteBooking, fetchAnalytics } from "@/lib/api";
import AdminPerformances from "@/components/AdminPerformances";
import AdminRad from "@/components/AdminRad";

const EVENT_LABELS = {
  goochelshow: "Goochelshow",
  tafelgoochelaar: "Tafelgoochelaar",
  circusanimatie: "Circusanimatie",
  suikerspin: "Suikerspin",
  anders: "Anders",
};

const STATUSES = [
  { value: "new", label: "Nieuw", color: "bg-mystic/30 text-mystic-light border-mystic/40" },
  { value: "contacted", label: "Gecontacteerd", color: "bg-gold/20 text-gold border-gold/40" },
  { value: "confirmed", label: "Bevestigd", color: "bg-green-500/20 text-green-400 border-green-500/40" },
  { value: "cancelled", label: "Geannuleerd", color: "bg-red-500/20 text-red-400 border-red-500/40" },
];

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("magicseppe_admin_token") || "");
  const [password, setPassword] = useState("");
  const [bookings, setBookings] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async (t) => {
    setLoading(true);
    try {
      const [bookingsData, analyticsData] = await Promise.all([
        fetchBookings(t),
        fetchAnalytics(t).catch(() => null),
      ]);
      setBookings(bookingsData);
      setAnalytics(analyticsData);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("magicseppe_admin_token");
        setToken("");
        toast.error("Sessie verlopen. Log opnieuw in.");
      } else {
        toast.error("Laden mislukt.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const res = await adminLogin(password);
      localStorage.setItem("magicseppe_admin_token", res.token);
      setToken(res.token);
      toast.success("Welkom terug, Seppe!");
    } catch (err) {
      toast.error("Ongeldig wachtwoord.");
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("magicseppe_admin_token");
    setToken("");
    setBookings([]);
    setSelected(null);
  };

  const handleStatusChange = async (id, status) => {
    try {
      const updated = await updateBookingStatus(id, status, token);
      setBookings((list) => list.map((b) => (b.id === id ? updated : b)));
      if (selected?.id === id) setSelected(updated);
      toast.success("Status bijgewerkt.");
    } catch {
      toast.error("Bijwerken mislukt.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Aanvraag definitief verwijderen?")) return;
    try {
      await deleteBooking(id, token);
      setBookings((list) => list.filter((b) => b.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Verwijderd.");
    } catch {
      toast.error("Verwijderen mislukt.");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-night flex items-center justify-center p-6 font-body" data-testid="admin-login-page">
        <div className="absolute inset-0 star-field opacity-40 pointer-events-none" />
        <form
          onSubmit={handleLogin}
          className="relative glass-strong rounded-3xl p-10 w-full max-w-md"
          data-testid="admin-login-form"
        >
          <div className="flex items-center gap-2 mb-8">
            <Sparkles className="w-6 h-6 text-gold" />
            <span className="font-display text-2xl font-bold">
              Magic <span className="gold-text">Seppe</span>
            </span>
          </div>
          <h1 className="font-display text-3xl font-bold mb-2">Admin</h1>
          <p className="text-[#B5A9C9] text-sm mb-8">
            Log in om aanvragen te bekijken.
          </p>

          <div className="space-y-2 mb-6">
            <Label className="text-xs uppercase tracking-widest text-[#B5A9C9]">Wachtwoord</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gold" />
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="admin-password-input"
                required
                className="pl-10 bg-night-surface/60 border-gold/25 text-[#F8F5FF] h-12"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loggingIn}
            data-testid="admin-login-submit"
            className="w-full bg-gold text-night-deep hover:bg-gold-light font-semibold h-12 rounded-full"
          >
            {loggingIn ? "Bezig..." : "Inloggen"}
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-night text-[#F8F5FF] font-body" data-testid="admin-dashboard">
      <header className="border-b border-gold/15 glass-strong sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-gold" />
            <span className="font-display text-lg font-bold">
              Magic <span className="gold-text">Seppe</span> · Admin
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => load(token)}
              variant="ghost"
              size="sm"
              data-testid="admin-refresh"
              className="text-[#B5A9C9] hover:text-gold hover:bg-gold/10"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button
              onClick={handleLogout}
              variant="ghost"
              size="sm"
              data-testid="admin-logout"
              className="text-[#B5A9C9] hover:text-gold hover:bg-gold/10"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Uitloggen
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <AdminPerformances token={token} />
        <AdminRad token={token} />

        {/* ---- Analytics summary ---- */}
        {analytics && (
          <section className="mb-12" data-testid="admin-analytics-section">
            <div className="flex items-center gap-3 mb-5">
              <BarChart3 className="w-5 h-5 text-gold" />
              <h2 className="font-display text-2xl font-bold">Statistieken</h2>
              <span className="text-xs text-[#7A6C96] uppercase tracking-widest">laatste 30 dagen</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
              <StatCard
                icon={Eye}
                label="Bezoeken vandaag"
                value={analytics.visits.today}
                sub={`${analytics.unique_visitors.today} unieke bezoekers`}
                testid="stat-visits-today"
              />
              <StatCard
                icon={Eye}
                label="Bezoeken deze week"
                value={analytics.visits.week}
                sub={`${analytics.unique_visitors.week} unieke`}
                testid="stat-visits-week"
              />
              <StatCard
                icon={MessageCircle}
                label="WhatsApp clicks"
                value={analytics.whatsapp_clicks.total}
                sub={`${analytics.whatsapp_clicks.week} deze week`}
                color="text-green-400"
                testid="stat-whatsapp"
              />
              <StatCard
                icon={Sparkles}
                label="Formulier geopend"
                value={analytics.booking_opens.total}
                sub={`${analytics.booking_submits.total} aanvragen ingediend`}
                color="text-mystic-light"
                testid="stat-booking-opens"
              />
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div className="md:col-span-2 glass rounded-2xl p-6" data-testid="analytics-daily-chart">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-xs uppercase tracking-widest text-[#B5A9C9]">Bezoeken per dag (14d)</div>
                  <div className="text-xs text-[#7A6C96]">grijs = totaal · goud = unieke</div>
                </div>
                <DailyBars data={analytics.daily} />
              </div>
              <div className="glass rounded-2xl p-6" data-testid="analytics-devices">
                <div className="text-xs uppercase tracking-widest text-[#B5A9C9] mb-4">Toestellen (30d)</div>
                {Object.keys(analytics.devices).length === 0 ? (
                  <p className="text-sm text-[#7A6C96]">Nog geen data.</p>
                ) : (
                  <div className="space-y-4">
                    {(() => {
                      const total = Object.values(analytics.devices).reduce((a, b) => a + b, 0) || 1;
                      return Object.entries(analytics.devices).map(([dev, cnt]) => {
                        const pct = Math.round((cnt / total) * 100);
                        const Icon = dev === "mobile" ? Smartphone : Monitor;
                        return (
                          <div key={dev}>
                            <div className="flex items-center justify-between text-sm mb-1">
                              <span className="flex items-center gap-2 capitalize text-[#F8F5FF]">
                                <Icon className="w-4 h-4 text-gold" />
                                {dev === "mobile" ? "Mobiel" : dev === "desktop" ? "Desktop" : dev}
                              </span>
                              <span className="text-[#7A6C96]">{cnt} · {pct}%</span>
                            </div>
                            <div className="h-2 bg-night-surface rounded-full overflow-hidden">
                              <div
                                className="h-full bg-gradient-to-r from-mystic to-gold"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold">Aanvragen</h1>
            <p className="text-[#B5A9C9] mt-2">
              {bookings.length} {bookings.length === 1 ? "aanvraag" : "aanvragen"} totaal
            </p>
          </div>
          <div className="flex items-center gap-3 text-sm text-[#B5A9C9]">
            {STATUSES.map((s) => (
              <div key={s.value} className="flex items-center gap-2">
                <span className={`inline-block w-2 h-2 rounded-full ${s.color.split(" ")[0]}`} />
                {s.label}: {bookings.filter((b) => b.status === s.value).length}
              </div>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className={`${selected ? "lg:col-span-2" : "lg:col-span-3"} glass-strong rounded-2xl overflow-hidden`}>
            <Table data-testid="admin-bookings-table">
              <TableHeader>
                <TableRow className="border-gold/15 hover:bg-transparent">
                  <TableHead className="text-gold uppercase text-xs tracking-widest">Naam</TableHead>
                  <TableHead className="text-gold uppercase text-xs tracking-widest">Show</TableHead>
                  <TableHead className="text-gold uppercase text-xs tracking-widest">Datum</TableHead>
                  <TableHead className="text-gold uppercase text-xs tracking-widest">Status</TableHead>
                  <TableHead className="text-gold uppercase text-xs tracking-widest text-right">Acties</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bookings.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-12 text-[#7A6C96]">
                      Nog geen aanvragen ontvangen.
                    </TableCell>
                  </TableRow>
                ) : (
                  bookings.map((b) => {
                    const statusMeta = STATUSES.find((s) => s.value === b.status) || STATUSES[0];
                    return (
                      <TableRow
                        key={b.id}
                        onClick={() => setSelected(b)}
                        className={`border-gold/10 cursor-pointer hover:bg-gold/5 transition-colors ${
                          selected?.id === b.id ? "bg-gold/10" : ""
                        }`}
                        data-testid={`booking-row-${b.id}`}
                      >
                        <TableCell className="font-medium">{b.name}</TableCell>
                        <TableCell className="text-[#B5A9C9]">{EVENT_LABELS[b.event_type] || b.event_type}</TableCell>
                        <TableCell className="text-[#B5A9C9]">
                          {format(new Date(b.event_date), "d MMM yyyy", { locale: nl })}
                        </TableCell>
                        <TableCell>
                          <Badge className={`${statusMeta.color} border font-normal`}>
                            {statusMeta.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(b.id);
                            }}
                            variant="ghost"
                            size="sm"
                            className="text-red-400/70 hover:text-red-400 hover:bg-red-500/10"
                            data-testid={`booking-delete-${b.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {selected && (
            <aside className="glass-strong rounded-2xl p-6 h-fit sticky top-28" data-testid="booking-detail-panel">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="text-xs uppercase tracking-widest text-gold mb-1">Aanvraag details</div>
                  <h3 className="font-display text-2xl font-bold">{selected.name}</h3>
                </div>
                <button
                  onClick={() => setSelected(null)}
                  className="text-[#7A6C96] hover:text-gold text-2xl leading-none"
                  aria-label="Sluiten"
                >×</button>
              </div>

              <div className="space-y-4 text-sm mb-6">
                <DetailRow icon={Mail} label="E-mail" value={<a href={`mailto:${selected.email}`} className="text-gold hover:underline">{selected.email}</a>} />
                <DetailRow icon={Phone} label="Telefoon" value={<a href={`tel:${selected.phone}`} className="text-gold hover:underline">{selected.phone}</a>} />
                <DetailRow icon={Calendar} label="Eventdatum" value={format(new Date(selected.event_date), "PPP", { locale: nl })} />
                <DetailRow icon={Sparkles} label="Show" value={EVENT_LABELS[selected.event_type] || selected.event_type} />
                <DetailRow icon={MapPin} label="Locatie" value={selected.location} />
                {selected.guests && <DetailRow icon={Users} label="Gasten" value={selected.guests} />}
              </div>

              {selected.message && (
                <div className="mb-6">
                  <div className="text-xs uppercase tracking-widest text-[#7A6C96] mb-2">Bericht</div>
                  <p className="text-[#B5A9C9] text-sm leading-relaxed glass rounded-xl p-4">
                    {selected.message}
                  </p>
                </div>
              )}

              <div className="mb-4">
                <Label className="text-xs uppercase tracking-widest text-[#7A6C96] mb-2 block">Status wijzigen</Label>
                <Select
                  value={selected.status}
                  onValueChange={(v) => handleStatusChange(selected.id, v)}
                >
                  <SelectTrigger
                    data-testid="detail-status-select"
                    className="bg-night-surface/60 border-gold/25 text-[#F8F5FF]"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-night-surface border-gold/30 text-[#F8F5FF]">
                    {STATUSES.map((s) => (
                      <SelectItem key={s.value} value={s.value} className="focus:bg-gold/20">
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="text-xs text-[#7A6C96]">
                Ontvangen op {format(new Date(selected.created_at), "PPpp", { locale: nl })}
              </div>
            </aside>
          )}
        </div>
      </main>
    </div>
  );
}

function DetailRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-4 h-4 text-gold mt-1 shrink-0" />
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-widest text-[#7A6C96]">{label}</div>
        <div className="text-[#F8F5FF] break-words">{value}</div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color = "text-gold", testid }) {
  return (
    <div className="glass rounded-2xl p-5 hover:border-gold/40 transition-colors duration-300" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <Icon className={`w-5 h-5 ${color}`} />
        <span className="text-[10px] uppercase tracking-widest text-[#7A6C96]">{label}</span>
      </div>
      <div className="font-display text-3xl font-bold text-[#F8F5FF]">{value}</div>
      {sub && <div className="text-xs text-[#7A6C96] mt-1">{sub}</div>}
    </div>
  );
}

function DailyBars({ data }) {
  if (!data || data.length === 0) {
    return <p className="text-sm text-[#7A6C96]">Nog geen bezoekers geregistreerd.</p>;
  }
  const max = Math.max(...data.map((d) => d.visits), 1);
  return (
    <div className="flex items-end gap-2 h-32">
      {data.map((d) => {
        const vh = Math.max(4, (d.visits / max) * 100);
        const uh = Math.max(3, (d.uniques / max) * 100);
        const dayLabel = d.date.slice(5); // MM-DD
        return (
          <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group">
            <div className="w-full flex items-end gap-0.5 h-full">
              <div
                className="flex-1 bg-[#7A6C96]/40 rounded-t group-hover:bg-[#7A6C96]/70 transition-colors"
                style={{ height: `${vh}%` }}
                title={`${d.date}: ${d.visits} bezoeken`}
              />
              <div
                className="flex-1 bg-gradient-to-t from-gold-dark to-gold rounded-t group-hover:from-gold group-hover:to-gold-light transition-colors"
                style={{ height: `${uh}%` }}
                title={`${d.date}: ${d.uniques} unieke`}
              />
            </div>
            <div className="text-[10px] text-[#7A6C96]">{dayLabel}</div>
          </div>
        );
      })}
    </div>
  );
}
